"use client";

import { useState, useMemo } from "react";
import {
  Paper,
  Text,
  Collapsible,
  TbMapPin,
  TbBuilding,
  CountryFlag,
} from "@forestsync/ui";
import { useTranslations } from "next-intl";
import { CarbonSinkRow } from "./common/CarbonSinkRow";
import { type CarbonSink } from "@/types/glassroom";
import { getCountryCode, getCountryName } from "@forestsync/db";

interface CarbonSinkListProps {
  sinks: CarbonSink[];
}

export function CarbonSinkList({ sinks }: CarbonSinkListProps) {
  const t = useTranslations();
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);

  // Build grouped structure for locations
  const locationGroups = useMemo(() => {
    const grouped = sinks.reduce((acc: any, item) => {
      // Normalize the key so mixed-format DB values (e.g. "SE" vs "Sweden")
      // group under a single country node instead of splitting the tree.
      const country = getCountryName(getCountryCode(item.country));
      if (!acc[country]) {
        acc[country] = {};
      }
      if (!acc[country][item.region]) {
        acc[country][item.region] = {};
      }
      if (!acc[country][item.region][item.municipality || "Unknown"]) {
        acc[country][item.region][item.municipality || "Unknown"] = [];
      }
      acc[country][item.region][item.municipality || "Unknown"].push(item);
      return acc;
    }, {});

    return grouped;
  }, [sinks]);

  if (sinks.length === 0) {
    return (
      <Paper withBorder p="md" radius="md">
        <Text className="text-gray-500 dark:text-gray-400 text-center">
          {t("glassroom.noItemsFound")}
        </Text>
      </Paper>
    );
  }

  return (
    <Paper withBorder p={{ base: "xs", sm: "md" }} radius="md">
      <div className="space-y-2">
        {Object.entries(locationGroups)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([country, regions]: [string, any]) => {
            const countryTotal = Object.values(regions).reduce(
              (sum: number, municipalities: any) =>
                sum +
                Object.values(municipalities).reduce(
                  (mSum: number, items: any) => mSum + items.length,
                  0,
                ),
              0,
            );

            return (
              <Collapsible
                key={country}
                label={getCountryName(getCountryCode(country))}
                count={countryTotal}
                icon={<CountryFlag countryCode={getCountryCode(country)} />}
                depth={0}
                color="blue"
              >
                {Object.entries(regions)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([region, municipalities]: [string, any]) => {
                    const regionTotal = Object.values(municipalities).reduce(
                      (sum: number, items: any) => sum + items.length,
                      0,
                    );

                    return (
                      <Collapsible
                        key={`${country}-${region}`}
                        label={region}
                        count={regionTotal}
                        icon={<TbMapPin size={18} className="text-green-500" />}
                        depth={1}
                        color="green"
                      >
                        {Object.entries(municipalities)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([municipality, items]: [string, any]) => {
                            const locationKey = `${country}-${region}-${municipality}`;
                            const isSelected = selectedLocation === locationKey;

                            return (
                              <Collapsible
                                key={locationKey}
                                label={municipality}
                                count={items.length}
                                icon={
                                  <TbBuilding
                                    size={18}
                                    className="text-orange-500"
                                  />
                                }
                                depth={2}
                                color="orange"
                                isSelected={isSelected}
                                onClick={() => setSelectedLocation(locationKey)}
                                defaultExpanded={isSelected}
                              >
                                <div className="space-y-0">
                                  {items.map(
                                    (sink: CarbonSink, index: number) => (
                                      <CarbonSinkRow
                                        key={sink.refId}
                                        sink={sink}
                                      />
                                    ),
                                  )}
                                </div>
                              </Collapsible>
                            );
                          })}
                      </Collapsible>
                    );
                  })}
              </Collapsible>
            );
          })}
      </div>
    </Paper>
  );
}
